import React, { Dispatch, SetStateAction, useCallback } from 'react'
import styled from 'styled-components/macro'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { useLinkPolicyDocsCallback } from 'src/pages/Policies/Policies.hooks'
import { useGooglePicker } from 'src/lib/Linkers/Linkers.hooks'
import { TextButton } from '../../Reusable/Buttons/TextButton'
import {
  TypeButton,
  TypeRow,
  TypeSelectorContainer,
} from '../FileType/TypeSelector.styles'
import { ReactComponent as LinkIcon } from '../../../components/Icons/assets/permalink-icon.svg'
import { ReactComponent as FileUploadIcon } from '../../../components/Icons/assets/file-upload-icon.svg'
import { DocumentFormData, DocumentType } from './AddDocumentForm'
import { GoogleDriveIcon } from './AddDocumentForm.styles'

type DocumentTypeSelectorProps = {
  setFormData: Dispatch<SetStateAction<DocumentFormData>>
  type: DocumentType
  setType: Dispatch<SetStateAction<DocumentType>>
  hide: () => void
  policyId: string
}

const StyledTypeButton = styled(TypeButton)`
  margin: 0;
  cursor: pointer;
`

export function DocumentTypeSelector({
  setFormData,
  type,
  setType,
  hide,
  policyId,
}: DocumentTypeSelectorProps): JSX.Element {
  const isGoogleDriveEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.GOOGLE_DRIVE_AUTH,
  )
  const openPicker = useGooglePicker()
  const linkPolicyDocs = useLinkPolicyDocsCallback(policyId)
  const clearText = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      setFormData((state) => {
        const newState: DocumentFormData = {
          ...state,
          mime: '',
          body: '',
        }
        return newState
      })
      if (type !== DocumentType.LINK) setType(DocumentType.NULL)
    },
    [setFormData, type, setType],
  )

  if (type !== DocumentType.NULL) {
    return <TextButton onClick={clearText}>Clear</TextButton>
  }

  return (
    <TypeSelectorContainer>
      <span>You can use any of these as Evidence Details.</span>
      <TypeRow>
        <TypeButton
          onClick={(e) => {
            e.preventDefault()
            setFormData((state) => ({ ...state, mime: 'text/uri-list' }))
            setType(DocumentType.LINK)
          }}
        >
          <LinkIcon width="48px" height="48px" />
          <span>Paste Link</span>
        </TypeButton>
        <StyledTypeButton
          as="label"
          htmlFor="evidence-file-upload"
          onKeyPress={() => {
            const uploadInput = document.getElementById('evidence-file-upload')
            if (uploadInput) uploadInput.click()
          }}
        >
          <FileUploadIcon />
          <span>Attach File</span>
          <input
            hidden
            type="file"
            id="evidence-file-upload"
            accept="image/png, image/jpeg, .rtf, .pdf, .doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .pages, .csv, .xlsx, .xls, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .ppt, .pptx, .key, .numbers"
            onChange={(e) => {
              e.preventDefault()
              const file = e.target.files?.[0]

              if (file) {
                let fileType = file.type
                if (!fileType) {
                  const split = file.name.split('.')
                  const extension = split[split.length - 1]
                  if (extension === 'key') {
                    fileType = 'application/vnd.apple.keynote'
                  } else if (extension === 'numbers') {
                    fileType = 'application/vnd.apple.numbers'
                  } else if (extension === 'pages') {
                    fileType = 'application/vnd.apple.pages'
                  }
                }

                setFormData((state) => {
                  const newState: DocumentFormData = {
                    ...state,
                    mime: fileType,
                    body: file,
                  }
                  return newState
                })
                setType(DocumentType.FILE)
              }
            }}
          />
        </StyledTypeButton>
        {isGoogleDriveEnabled ? (
          <TypeButton
            onClick={async (e) => {
              e.preventDefault()
              const isShown = await openPicker(linkPolicyDocs)
              if (isShown) {
                hide()
              }
            }}
          >
            <GoogleDriveIcon />
            <span>Google Drive</span>
          </TypeButton>
        ) : (
          <></>
        )}
      </TypeRow>
    </TypeSelectorContainer>
  )
}
